import type {
  FrameTargetAnnotationDraft,
  NormalizedAnnotationPoint,
  TrackingPreviewContourPoint,
} from "@carbon6/c6-frontend-design/annotation";
import type { AnnotationFrameTargetWrite, AnnotationSetFrameTargetView } from "@/api";

export type AnnotationFrameSize = Readonly<{ width: number; height: number }>;

export class AnnotationFrameGeometryRegistry {
  private readonly sizes = new Map<string, AnnotationFrameSize>();

  bind(frameId: string, size: AnnotationFrameSize): void {
    if (size.width <= 0 || size.height <= 0) throw new RangeError("frame size must be positive");
    const existing = this.sizes.get(frameId);
    if (
      existing !== undefined &&
      (existing.width !== size.width || existing.height !== size.height)
    ) {
      throw new Error("annotation frame geometry changed");
    }
    this.sizes.set(frameId, { ...size });
  }

  get(frameId: string): AnnotationFrameSize | null {
    return this.sizes.get(frameId) ?? null;
  }

  clear(): void {
    this.sizes.clear();
  }
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

export function pixelToNormalized(pixel: number, extent: number): number {
  return extent <= 1 ? 0 : clamp01(pixel / (extent - 1));
}

export function normalizedToPixel(normalized: number, extent: number): number {
  return extent <= 1 ? 0 : Math.round(clamp01(normalized) * (extent - 1));
}

export function frameIndexAtTimestamp(
  timestampSeconds: number,
  fps: number,
  numFrames: number,
): number {
  if (!Number.isFinite(fps) || fps <= 0) throw new RangeError("fps must be positive");
  if (!Number.isInteger(numFrames) || numFrames <= 0) {
    throw new RangeError("numFrames must be a positive integer");
  }
  return Math.min(numFrames - 1, Math.max(0, Math.round(timestampSeconds * fps)));
}

export function timestampAtFrameIndex(frameIndex: number, fps: number): number {
  if (!Number.isInteger(frameIndex) || frameIndex < 0) {
    throw new RangeError("frameIndex must be a non-negative integer");
  }
  if (!Number.isFinite(fps) || fps <= 0) throw new RangeError("fps must be positive");
  return frameIndex / fps;
}

export function wireTargetToPoints(
  target: Pick<AnnotationSetFrameTargetView, "points" | "labels">,
  size: AnnotationFrameSize,
  pointIdPrefix: string,
): NormalizedAnnotationPoint[] {
  return target.points.map((point, index) => ({
    id: `${pointIdPrefix}:p${index}`,
    kind: target.labels[index] === 1 ? "include" : "exclude",
    x: pixelToNormalized(point[0] ?? 0, size.width),
    y: pixelToNormalized(point[1] ?? 0, size.height),
  }));
}

export function draftTargetToWire(
  objId: number,
  target: Pick<FrameTargetAnnotationDraft, "points">,
  size: AnnotationFrameSize,
): AnnotationFrameTargetWrite {
  return {
    obj_id: objId,
    points: target.points.map((point) => [
      normalizedToPixel(point.x, size.width),
      normalizedToPixel(point.y, size.height),
    ]),
    labels: target.points.map((point) => (point.kind === "include" ? 1 : 0)),
  };
}

export function normalizeWireContour(
  contour: readonly { x: number; y: number }[],
  size: AnnotationFrameSize,
): TrackingPreviewContourPoint[] {
  return contour.map((point) => ({
    x: pixelToNormalized(point.x, size.width),
    y: pixelToNormalized(point.y, size.height),
  }));
}
